// ============================================
// PORTADAEXPEDIENTEPDF.JS — Caratula IMPRIMIBLE del expediente (folder fisico)
// ============================================
// UNA pagina carta que se engrapa al folder: encabezado con logo + folio grande
// + QR al expediente digital, datos clave, tabla "Seguimiento del proceso" (un
// renglon por paso del flujo; los hechos van con fecha/responsable reales, los
// pendientes con espacio para anotar A MANO) y observaciones generales.
//
// MISMO stack que cotizacionPDF (jsPDF + jspdf-autotable, SIN html2canvas):
// texto vectorial nitido para impresora y acentos correctos. Amigable con
// impresora: fondos claros, sin tintas pesadas. SIN MONTOS (decision de
// Ricardo: el dinero no va impreso; el QR lleva al dato digital).

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { EMISOR, LOGO_URL, cargarLogo } from './cotizacionPDF';

// Paleta de marca (hex, igual que cotizacionPDF; las CSS-vars no sirven en jsPDF).
const C = {
  azul: '#265F97', azulMedio: '#5E99D2', azulLinea: '#9EC2E5', azulFondo: '#DEEBF7',
  gris: '#3D4248', grisMedio: '#5E666E', grisClaro: '#9AA1A8',
  linea: '#C9CDD2', fondoSuave: '#F3F5F8', blanco: '#FFFFFF',
};

function fmtFecha(v) {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function fmtFechaHora(v) {
  const d = v instanceof Date ? v : new Date(v || Date.now());
  if (isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${fmtFecha(d)} ${hh}:${mi}`;
}

// datos = {
//   folio, titulo, clienteTexto, ubicacionTexto, tipoTrabajo, responsable,
//   fechaInicio, cotFolio (SOLO folio, sin monto),
//   pasos: [{ nombre, situacion: 'hecho'|'actual'|'pendiente'|'saltado'|'no_aplica',
//             fecha, responsable }],
//   qrDataUrl (PNG dataURL o null), impresoPor,
// }
// Devuelve el doc por si el llamador quiere otra salida; por default hace .save().
export async function generarPortadaExpedientePDF(datos, { guardar = true } = {}) {
  const d = datos || {};
  const pasos = Array.isArray(d.pasos) ? d.pasos : [];
  const logo = await cargarLogo(LOGO_URL);

  const doc = new jsPDF('p', 'mm', 'letter');
  const W = doc.internal.pageSize.getWidth();   // 215.9
  const H = doc.internal.pageSize.getHeight();  // 279.4
  const M = 14; // margen

  // ---- Banda superior de marca (delgada, tinta minima)
  doc.setFillColor(C.azul);
  doc.rect(0, 0, W, 3.5, 'F');

  // ---- Encabezado: logo + emisor (izquierda)
  let y = 11;
  if (logo) {
    const hL = 13, wL = hL * (logo.w / logo.h);
    doc.addImage(logo.dataUrl, 'PNG', M, y, wL, hL);
  } else {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(C.azul);
    doc.text('Beep', M, y + 9);
  }
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(C.grisMedio);
  const emisorLineas = [EMISOR.razonSocial, EMISOR.telefono ? `Tel: ${EMISOR.telefono}` : '', EMISOR.web].filter(Boolean);
  let ey = y + 17;
  emisorLineas.forEach((l) => { doc.text(String(l), M, ey); ey += 3.6; });

  // ---- Encabezado derecha: QR + "EXPEDIENTE" + folio grande
  const QR = 30; // lado del QR en mm
  const qrX = W - M - QR;
  if (d.qrDataUrl) {
    doc.addImage(d.qrDataUrl, 'PNG', qrX, y - 1, QR, QR);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(5.8); doc.setTextColor(C.grisClaro);
    doc.text('Escanea para abrir el expediente digital', qrX + QR / 2, y + QR + 2.4, { align: 'center' });
  }
  const xTitulo = d.qrDataUrl ? qrX - 6 : W - M;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(C.grisMedio);
  doc.text('EXPEDIENTE', xTitulo, y + 7, { align: 'right' });
  doc.setFont('helvetica', 'bold'); doc.setFontSize(21); doc.setTextColor(C.azul);
  doc.text(String(d.folio || '—'), xTitulo, y + 16, { align: 'right' });

  // ---- Separador
  const ySep = Math.max(ey, y + QR + 5);
  doc.setDrawColor(C.azulLinea); doc.setLineWidth(0.5);
  doc.line(M, ySep, W - M, ySep);

  // ---- DATOS (grid 2 columnas x 3 filas via autotable, tema plano)
  const clienteCell = [d.clienteTexto || 'Sin cliente', d.ubicacionTexto || ''].filter(Boolean).join('\n');
  const filasDatos = [
    ['CLIENTE', clienteCell, 'TIPO DE TRABAJO', d.tipoTrabajo || '—'],
    ['PROYECTO', d.titulo || 'Sin titulo', 'RESPONSABLE ACTUAL', d.responsable || 'Sin responsable'],
    ['FECHA DE INICIO', fmtFecha(d.fechaInicio) || '—', 'COTIZACION VINCULADA', d.cotFolio || 'Sin cotizacion'],
  ];
  autoTable(doc, {
    startY: ySep + 4,
    body: filasDatos,
    theme: 'plain',
    margin: { left: M, right: M },
    styles: { font: 'helvetica', cellPadding: { top: 1.6, bottom: 1.6, left: 0, right: 3 }, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 30, fontSize: 6.8, textColor: C.grisClaro, fontStyle: 'bold' },
      1: { cellWidth: 66, fontSize: 9.5, textColor: C.gris, fontStyle: 'bold' },
      2: { cellWidth: 36, fontSize: 6.8, textColor: C.grisClaro, fontStyle: 'bold' },
      3: { cellWidth: 'auto', fontSize: 9.5, textColor: C.gris, fontStyle: 'bold' },
    },
  });
  let yTabla = doc.lastAutoTable.finalY + 6;

  // ---- TABLA "SEGUIMIENTO DEL PROCESO"
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(C.azul);
  doc.text('SEGUIMIENTO DEL PROCESO', M, yTabla);
  yTabla += 2.5;

  // Altura de renglon: comoda para escribir con pluma (8-11.5mm) y que TODO
  // quepa en UNA pagina: se reparte el espacio restante entre los pasos.
  const ALTO_OBS = 36;  // titulo + 4 renglones de observaciones
  const ALTO_PIE = 12;
  const disponible = H - yTabla - ALTO_OBS - ALTO_PIE - 8 /* head */ - 4;
  const altoFila = Math.max(8, Math.min(11.5, disponible / Math.max(pasos.length, 1)));

  const body = pasos.map((p) => {
    const hecho = p.situacion === 'hecho';
    const noAplica = p.situacion === 'saltado' || p.situacion === 'no_aplica';
    return [
      { content: p.nombre || '', styles: { fontStyle: 'bold', textColor: noAplica ? C.grisClaro : (p.situacion === 'actual' ? C.azul : C.gris) } },
      { content: hecho ? fmtFecha(p.fecha) : '', styles: { textColor: C.grisMedio, halign: 'center' } },
      { content: hecho ? (p.responsable || '') : '', styles: { textColor: C.grisMedio } },
      { content: noAplica ? 'No aplica en este expediente' : '', styles: { textColor: C.grisClaro, fontStyle: 'italic', fontSize: 6.8 } },
    ];
  });

  autoTable(doc, {
    startY: yTabla,
    head: [['Paso', 'Fecha', 'Responsable', 'Anotaciones / Firma']],
    body,
    theme: 'grid',
    margin: { left: M, right: M },
    styles: {
      font: 'helvetica', fontSize: 8.3, cellPadding: { top: 2, bottom: 2, left: 2, right: 2 },
      lineColor: C.linea, lineWidth: 0.2, minCellHeight: altoFila, valign: 'middle',
      textColor: C.gris,
    },
    headStyles: {
      fillColor: C.azulFondo, textColor: C.azul, fontStyle: 'bold', fontSize: 8,
      minCellHeight: 7, valign: 'middle',
    },
    columnStyles: {
      0: { cellWidth: 46 },
      1: { cellWidth: 24, halign: 'center' },
      2: { cellWidth: 40 },
      3: { cellWidth: 'auto' },
    },
  });

  // ---- OBSERVACIONES GENERALES (renglones para escribir a mano)
  let yObs = doc.lastAutoTable.finalY + 6;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(C.azul);
  doc.text('OBSERVACIONES GENERALES', M, yObs);
  yObs += 6.5;
  doc.setDrawColor(C.linea); doc.setLineWidth(0.25);
  for (let i = 0; i < 4; i++) {
    doc.line(M, yObs, W - M, yObs);
    yObs += 7;
  }

  // ---- PIE: quien imprimio + cuando + ubicacion
  doc.setDrawColor(C.azulLinea); doc.setLineWidth(0.4);
  doc.line(M, H - 10, W - M, H - 10);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(C.grisMedio);
  const piePartes = [
    `Impreso el ${fmtFechaHora(new Date())}${d.impresoPor ? ` por ${d.impresoPor}` : ''}`,
    d.ubicacionTexto || '',
  ].filter(Boolean).join('   ·   ');
  doc.text(piePartes, M, H - 5.5);
  doc.text('app.beepmexico.com', W - M, H - 5.5, { align: 'right' });

  if (guardar) doc.save(`Portada-${String(d.folio || 'expediente').replace(/[^\w.-]+/g, '_')}.pdf`);
  return doc;
}
